import { ApiErrorSchema, type ApiError } from '@/contracts/errors';
import { localizeApiError } from '@/app/localization';

export class ApiRequestError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly recoverable: boolean;

  constructor(apiError: ApiError) {
    super(localizeApiError(apiError.error.code, apiError.error.message));
    this.code = apiError.error.code;
    this.requestId = apiError.error.requestId;
    this.recoverable = apiError.error.recoverable;
  }
}

async function parseErrorResponse(response: Response, path: string): Promise<never> {
  const body: unknown = await response.json().catch(() => null);
  const parsed = ApiErrorSchema.safeParse(body);
  if (parsed.success) {
    throw new ApiRequestError(parsed.data);
  }
  throw new Error(`Request to ${path} failed with status ${response.status}`);
}

/**
 * Minimal typed fetch wrappers (SPEC-001 foundation, extended by SPEC-002
 * for cookie-session auth calls). Feature code must go through these
 * instead of calling `fetch('/api/...')` directly, so error normalization,
 * credentials and CSRF-relevant headers stay centralized.
 */
export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  return (await response.json()) as T;
}

export async function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  return (await response.json()) as T;
}

export async function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  return (await response.json()) as T;
}

export async function apiPut<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  return (await response.json()) as T;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  return (await response.json()) as T;
}
